import { useSelector } from '@xstate/store/react';
import { useParams } from 'react-router-dom';

import {
  useAppointmentTranscriptLength,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { recordingDurationStore } from '@eluve/stores';
import { MIN_VALID_TRANSCRIPT_LENGTH } from '@eluve/utils';

import { useTranscriptionMachineSelector } from './useTranscriptionMachine';

// Minimum cumulative audio (seconds) before a note can be generated. Note gen is
// blocked while strictly under this (00:00 / nothing captured included).
export const MIN_NOTE_RECORDING_SECONDS = 10;

/**
 * Whether the current appointment's recording is too short to generate a note.
 * Reads the duration the timer publishes (TranscriptionWaveform), so the guard
 * always matches the on-screen time. Fail-closed: blocks unless we have a
 * confirmed ≥ minimum for this appointment — so a still-loading duration and a
 * stale value from another appointment block.
 *
 * Allowed when ANY of:
 *  - a summary already exists — regenerating an existing note, audio was long
 *    enough before, OR
 *  - audio ≥ 10s (the proxy; covers transcription lag during a live recording), OR
 *  - the transcript is long enough that the server won't reject it as too short
 *    (≥ MIN_VALID_TRANSCRIPT_LENGTH) — covers content added without recording,
 *    e.g. the admin "Add to transcript" tool or an imported conversation.
 * A short recording (a few words, under the transcript threshold) stays blocked.
 */
export const useIsRecordingTooShortForNote = (): boolean => {
  // Regenerating an existing note — the prior summary proves the audio was long
  // enough, and covers the transcript-cache-still-loading race. Mirrors the
  // embedded-recording guard.
  const { isSummaryAvailable } = useSummary();
  const { appointmentId: appointmentIdFromParams } = useParams<{
    appointmentId?: string;
  }>();
  const currentAppointmentId =
    useTranscriptionMachineSelector((s) => s.context.sessionAppointmentId) ??
    appointmentIdFromParams ??
    '';

  // Two stable reads (a boolean + the id) instead of the whole context, so the
  // controls re-render only when the outcome can change, not every 1s tick.
  const storeAppointmentId = useSelector(
    recordingDurationStore,
    (s) => s.context.appointmentId,
  );
  const isAudioLongEnough = useSelector(
    recordingDurationStore,
    (s) => s.context.durationSeconds >= MIN_NOTE_RECORDING_SECONDS,
  );

  const transcriptLength = useAppointmentTranscriptLength(currentAppointmentId);

  // Transcript is read for the current appointment directly, so it stands on its
  // own. The audio path requires the store reading to be for this appointment
  // (the duration is shared, so a stale value from another appointment can't
  // unblock here).
  const allowed =
    isSummaryAvailable ||
    transcriptLength >= MIN_VALID_TRANSCRIPT_LENGTH ||
    (storeAppointmentId === currentAppointmentId && isAudioLongEnough);

  return !allowed;
};
